import { BottomSheetTabsListItemProps } from '@/components/molecules/BottomSheet/BottomSheetTabs/BottomSheetTabsList';
import { ReviewTagExposure } from '@/models/review/review';
import {
  GetAgreementsByLocationCategoryQuery,
  GetAgreementsForQuestionCategoryQuery,
  GetMagazineRelatedQuestionCardsQuery,
  GetNewestQuestionCardCursorPageQuery,
  GetQnaCategoryGroupQuery,
  GetRelatedQuestionQuery,
  QnaAnswerReviewTagExposureType,
  QnaAnswerReviewTagsQuery,
  QnaAnswerReviewsQuery,
} from '@/types/bff/gql-schema';
import {
  AgreementByLocationCategory,
  Policy,
  QnaAnswerReviewTagExposureTypeVo,
  QnaCategoryGroup,
  RealtimeQnaCardVo,
  RelatedQnaVo,
} from '@/types/qna';
import { absoluteDate, displayedAt, getQnaAnswerStatus, sortImageBlindTypeFormat } from '@/utils/format';

export const parseRelatedQnaVo = (qnaDetail: GetRelatedQuestionQuery): RelatedQnaVo[] => {
  if (qnaDetail) {
    return qnaDetail.similarSearchQuestionCardCursorPageRequest.elements.map((element) => {
      return {
        ...element,
        question: {
          ...element.question,
          attachments: sortImageBlindTypeFormat(element.question.attachments),
        },
        answeredStatusText: getQnaAnswerStatus(element.answeredStatus),
        questionCreatedAt: absoluteDate(element.question.createdAt),
      };
    });
  }
  return [] as RelatedQnaVo[];
};

export const parseRealtimeQnaCardVo = (data: Array<GetNewestQuestionCardCursorPageQuery>): RealtimeQnaCardVo[] => {
  if (data) {
    return data
      .flatMap((page) => page.newestQuestionCardCursorPage.elements)
      .map((element) => {
        return {
          ...element,
          question: {
            ...element.question,
            attachments: sortImageBlindTypeFormat(element.question.attachments),
          },
          questionCreatedAt: absoluteDate(element.question.createdAt),
          answerCreatedAt: element.answer ? displayedAt(element.answer.createdAt) : null,
          answeredStatusText: getQnaAnswerStatus(element.answeredStatus),
        };
      });
  }
  return [] as RealtimeQnaCardVo[];
};

export const parseQnaCategoryGroup = (data: GetQnaCategoryGroupQuery): QnaCategoryGroup => {
  const { questionCategoryGroups: categoryGroups } = data;

  const parsedCategories = () => {
    return categoryGroups.map((questionCategory) => {
      return {
        key: questionCategory.key,
        name: questionCategory.name,
        categories: questionCategory.questionCategories.map((category) => {
          return {
            ...category,
            key: questionCategory.key,
            title: category.name,
            content: category.description,
            iconImageUrl: category.iconImageUrl,
            value: category.questionCategoryId,
            checked: false,
          };
        }),
      };
    });
  };

  return {
    tabs: categoryGroups.map((category) => {
      const { key, name } = category;
      return {
        key,
        label: name,
        value: key,
      };
    }),
    tabsList: parsedCategories(),
    categories: parsedCategories().reduce((acc, cur) => {
      return acc.concat(cur.categories);
    }, [] as Array<BottomSheetTabsListItemProps>),
  };
};

export const parseMagazineRelatedQnaVo = (qna: GetMagazineRelatedQuestionCardsQuery): RelatedQnaVo[] => {
  if (qna) {
    return qna.magazineRelatedQuestionCards.map((element) => {
      return {
        ...element,
        question: {
          ...element.question,
          category: {
            ...element.question.category,
            name: '관련 의료진 상담',
          },
          attachments: sortImageBlindTypeFormat(element.question.attachments),
        },
        answeredStatusText: getQnaAnswerStatus(element.answeredStatus),
        questionCreatedAt: absoluteDate(element.question.createdAt),
      };
    });
  }
  return [] as RelatedQnaVo[];
};

export const parseAgreementsByCategory = (
  category: GetAgreementsByLocationCategoryQuery,
): AgreementByLocationCategory => {
  if (category) {
    return category.getAgreementsByCategory[0];
  }
  return {} as AgreementByLocationCategory;
};

export const parseAgreements = (agreements: GetAgreementsForQuestionCategoryQuery): Array<Policy> => {
  return agreements.getAgreementsByCategory as Array<Policy>;
};

export const convertReviewTagExposure = (exposureType: QnaAnswerReviewTagExposureType): ReviewTagExposure => {
  switch (exposureType) {
    case QnaAnswerReviewTagExposureType.Always:
      return ReviewTagExposure.ALWAYS;
    case QnaAnswerReviewTagExposureType.Positive:
      return ReviewTagExposure.POSITIVE;
    case QnaAnswerReviewTagExposureType.Negative:
      return ReviewTagExposure.NEGATIVE;
    default:
      return ReviewTagExposure.NONE;
  }
};

export const parseQnaAnswerReviewTagVo = (data: QnaAnswerReviewTagsQuery): QnaAnswerReviewTagExposureTypeVo[] => {
  return data.qnaAnswerReviewTags.map((reviewTag) => ({
    exposureType: convertReviewTagExposure(reviewTag.exposureType),
    id: reviewTag.id,
    text: reviewTag.text,
  }));
};

export const parseQnaAnswerReviewsVo = (data: QnaAnswerReviewsQuery) => {
  return data.qnaAnswerReviews.map((review) => {
    return {
      id: review.id,
      answerPid: review.answerPid,
      reviewTags: review.reviewTags,
      rating: review.rating,
      content: review.content,
    };
  });
};
